import { Layout, Tabs, TabsProps } from 'antd';
import React, { useMemo, useState } from 'react';
import {
  CasesCredentialVerificationV1ChecklistContainerCredentialVerificationCaseV1FieldsFragment,
  CredentialVerificationCaseV1Affirmations,
  CredentialVerificationCaseV1ApplicationReviewInput,
  CredentialVerificationCaseV1VerificationAffirmations
} from '../../../../../../generated';
import { AffirmationsChecklist, SharedCaseAffirmationsAudit } from '../../../../../shared/affirmations-checklist/affirmations-checklist';
import { ReviewDocumentsButtonContainer } from './review-documents-button-container';


export interface ChecklistProps {
  data: CasesCredentialVerificationV1ChecklistContainerCredentialVerificationCaseV1FieldsFragment;
  isSuccess: boolean;
  onSave: (values: CredentialVerificationCaseV1ApplicationReviewInput) => Promise<void>;
}

export const CredentialCaseAffirmationsFieldNames = {
  NAME_ACCEPTABLE: 'Name acceptable',
  TRANSLATION_ACCEPTABLE: 'Translation acceptable',
  CREDENTIAL_ACCEPTABLE: 'Credential acceptable',
  CREDENTIAL_DETAILS_ACCEPTABLE: 'Credential details acceptable',
  DOB_ACCEPTABLE: 'DOB acceptable',
  ENTITY_ACCEPTABLE: 'Entity acceptable',
  CREDENTIAL_SENT_FOR_VERIFICATION: 'Credential sent for verification',
  RETURNED_FROM_CORRECT_ENTITY: 'Returned from correct entity',
  VERIFICATION_METHOD_ACCEPTABLE: 'Verification method acceptable',
  VERIFICATION_COMPLETE: 'Verification complete',
  VERIFICATION_RESPONSE: 'Verification response in English or with translation',
  ENTITY_DETAIL_CORRECT: 'Entity detail correct',
  CREDENTIAL_RETURNED: 'Credential returned from entity',
  VERIFICATION_STATUS_COMPLETE: 'Verification status complete',
  VERIFICATION_PACKET: 'Verification component packet compiled'
};

export const Checklist: React.FC<ChecklistProps> = (props) => {
  const [activeTab, setActiveTab] = useState('1');

  const stripFieldsForAffirmations = (data: CredentialVerificationCaseV1Affirmations) => {
    if (!data) {
      return {
        isNameAcceptable: null,
        isTranslationAcceptable: null,
        isCredentialAcceptable: null,
        isCredentialDetailsAcceptable: null,
        isDateOfBirthAcceptable: null,
        isEntityAcceptable: null,
        isCredentialSentForVerification: null
      };
    }
    const { audit, __typename, ...rest } = data;
    return rest;
  };

  const stripFieldsForVerificationAffirmations = (data: CredentialVerificationCaseV1VerificationAffirmations) => {
    if (!data) {
      return {
        isReturnedFromCorrectEntity: null,
        isVerificationMethodAcceptable: null,
        isVerificationComplete: null,
        isVerificationInEnglishOrWithTranslation: null,
        isEntityDetailCorrect: null,
        isCredentialReturnedFromEntity: null,
        isVerificationStatusComplete: null,
        isVerificationComponentPacketCompiled: null
      };
    }
    const { audit, __typename, ...rest } = data;
    return rest;
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key);
  };

  const fieldNamesMap = useMemo(
    () =>
      new Map<string, string>([
        ['isNameAcceptable', CredentialCaseAffirmationsFieldNames.NAME_ACCEPTABLE],
        ['isTranslationAcceptable', CredentialCaseAffirmationsFieldNames.TRANSLATION_ACCEPTABLE],
        ['isCredentialAcceptable', CredentialCaseAffirmationsFieldNames.CREDENTIAL_ACCEPTABLE],
        ['isCredentialDetailsAcceptable', CredentialCaseAffirmationsFieldNames.CREDENTIAL_DETAILS_ACCEPTABLE],
        ['isDateOfBirthAcceptable', CredentialCaseAffirmationsFieldNames.DOB_ACCEPTABLE],
        ['isEntityAcceptable', CredentialCaseAffirmationsFieldNames.ENTITY_ACCEPTABLE],
        ['isCredentialSentForVerification', CredentialCaseAffirmationsFieldNames.CREDENTIAL_SENT_FOR_VERIFICATION],
        ['isReturnedFromCorrectEntity', CredentialCaseAffirmationsFieldNames.RETURNED_FROM_CORRECT_ENTITY],
        ['isVerificationMethodAcceptable', CredentialCaseAffirmationsFieldNames.VERIFICATION_METHOD_ACCEPTABLE],
        ['isVerificationComplete', CredentialCaseAffirmationsFieldNames.VERIFICATION_COMPLETE],
        ['isVerificationInEnglishOrWithTranslation', CredentialCaseAffirmationsFieldNames.VERIFICATION_RESPONSE],
        ['isEntityDetailCorrect', CredentialCaseAffirmationsFieldNames.ENTITY_DETAIL_CORRECT],
        ['isCredentialReturnedFromEntity', CredentialCaseAffirmationsFieldNames.CREDENTIAL_RETURNED],
        ['isVerificationStatusComplete', CredentialCaseAffirmationsFieldNames.VERIFICATION_STATUS_COMPLETE],
        ['isVerificationComponentPacketCompiled', CredentialCaseAffirmationsFieldNames.VERIFICATION_PACKET]
      ]),
    []
  );

  const tabItems: TabsProps['items'] = [
    {
      key: '1',
      label: 'Credential Review',
      children: (
        <AffirmationsChecklist
          affirmations={
            stripFieldsForAffirmations(
              props.data?.caseDetails?.applicationReview?.affirmations as CredentialVerificationCaseV1Affirmations
            ) as CredentialVerificationCaseV1Affirmations
          }
          audit={props.data?.caseDetails?.applicationReview?.affirmations?.audit as SharedCaseAffirmationsAudit}
          fieldName="affirmations"
          onSave={props.onSave}
          fieldNamesMap={fieldNamesMap}
          state={props.data.state}
          disabled={
            !props.data.isCurrentUserAssigned ||
            props.data.state === 'draft' ||
            props.data.state === 'decisionRendered' ||
            (props.data.state === 'revisionRequested' && props.data.isCurrentUserAssigned)
          }
          isSuccess={props.isSuccess}
        />
      )
    },
    {
      key: '2',
      label: 'Verification Review',
      children: (
        <AffirmationsChecklist
          affirmations={
            stripFieldsForVerificationAffirmations(
              props.data?.caseDetails?.applicationReview?.verificationAffirmations as CredentialVerificationCaseV1VerificationAffirmations
            ) as CredentialVerificationCaseV1VerificationAffirmations
          }
          audit={props.data?.caseDetails?.applicationReview?.verificationAffirmations?.audit as SharedCaseAffirmationsAudit}
          fieldName="verificationAffirmations"
          onSave={props.onSave}
          fieldNamesMap={fieldNamesMap}
          state={props.data.state}
          disabled={
            !props.data.isCurrentUserAssigned ||
            props.data.state === 'draft' ||
            props.data.state === 'decisionRendered' ||
            (props.data.state === 'revisionRequested' && props.data.isCurrentUserAssigned)
          }
          isSuccess={props.isSuccess}
        />
      )
    }
  ];
  return (
    <Layout
      style={{
        borderRadius: '10px'
      }}
    >
      <ReviewDocumentsButtonContainer caseId={props.data.id} />
      <Tabs defaultActiveKey="1" items={tabItems} activeKey={activeTab} onChange={handleTabChange} />
    </Layout>
  );
};
